import React from 'react';
import { Lock, Phone, Clock, AlertCircle, Scale, Gavel } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const CriminalPage = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Criminal Lawyer Pattaya Thailand | Urgent Legal Help for Expats"
        description="Criminal lawyer Pattaya - Urgent legal defence for expats and foreigners. Confidential criminal defence, immediate consultation. English speaking criminal attorney Thailand. Call now."
        keywords="Criminal Lawyer Pattaya, Criminal Defence Thailand, Expat Criminal Attorney Pattaya, English Speaking Lawyer Pattaya, Urgent Legal Help Pattaya, Law Firm Pattaya for Foreigners"
        path="/criminal"
        schemaType="criminal"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#000A22] via-[#001438] to-[#000A22] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 bg-red-900/30 border border-red-500 text-red-300 px-5 py-2.5 rounded-full font-medium text-sm">
              <AlertCircle className="w-4 h-4" />
              Urgent Legal Matter
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold">Criminal Lawyer Pattaya Thailand</h1>
            <p className="text-xl text-gray-300">Urgent Legal Defence for Expats & Foreigners</p>
          </div>
        </div>
      </section>

      {/* Urgency Bar */}
      <section className="bg-red-900 text-white py-6">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Clock className="w-8 h-8 flex-shrink-0" />
            <p className="text-lg font-semibold">
              Time is critical in criminal cases. Contact us immediately for urgent legal assistance.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-8 mb-16">
              <Card className="border-2 border-[#D4A74A] shadow-xl">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-bold text-[#000A22] mb-2">Confidential</h3>
                  <p className="text-gray-700">All matters handled with strict confidentiality</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#D4A74A] shadow-xl">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-bold text-[#000A22] mb-2">Urgent Response</h3>
                  <p className="text-gray-700">Immediate consultation available</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#D4A74A] shadow-xl">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gavel className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-bold text-[#000A22] mb-2">Expert Defence</h3>
                  <p className="text-gray-700">Experienced criminal law attorneys</p>
                </CardContent>
              </Card>
            </div>

            <div className="prose prose-lg max-w-none space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-[#000A22] mb-4">Professional Criminal Law Representation</h2>
                <p className="text-gray-700 leading-relaxed">
                  Facing criminal charges in Thailand can be an overwhelming and stressful experience, particularly for foreign nationals unfamiliar with the Thai legal system. At Pattaya Solicitors, we provide expert criminal law defence services with discretion, professionalism, and unwavering dedication to protecting your rights.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-[#000A22] mb-4">How We Can Help</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <span className="text-[#D4A74A] text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Immediate legal consultation and case assessment</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#D4A74A] text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Representation during police questioning</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#D4A74A] text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Bail application and court proceedings</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#D4A74A] text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Defence strategy development</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#D4A74A] text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Liaison with embassy officials if required</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[#D4A74A] text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Full court representation and appeals</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-[#000A22] mb-4">Who We Represent</h3>
                <p className="text-gray-700 leading-relaxed">
                  We represent both Thai nationals and foreign residents facing criminal charges in Thailand. Our team understands the complexities of the Thai criminal justice system and the unique challenges faced by international clients. We provide clear communication in English and ensure you understand your rights and options at every stage.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-[#000A22] mb-4">Our Approach</h3>
                <p className="text-gray-700 leading-relaxed">
                  Every criminal case is treated with the utmost seriousness and handled with complete confidentiality. We work diligently to build a strong defence, negotiate with prosecutors where appropriate, and fight for the best possible outcome. Your case will be handled by experienced attorneys who are registered with the Lawyer's Council of Thailand.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-red-900 mb-2">Time-Sensitive Matter</h4>
                    <p className="text-red-800">
                      Criminal cases require immediate action. The sooner you engage legal representation, the better we can protect your rights and interests. Do not delay – contact us now.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multiple CTAs */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h3 className="text-2xl font-bold text-[#000A22]">Call Us Immediately</h3>
            <p className="text-gray-700">Confidential consultation available now</p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                className="bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-semibold px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl"
                asChild
              >
                <a href="tel:+6638412415">
                  <Phone className="w-5 h-5 mr-2" />
                  +66 (38) 412-415
                </a>
              </Button>
              
              <Button 
                size="lg"
                className="bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-semibold px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl"
                asChild
              >
                <a href="tel:+66818383869">
                  <Phone className="w-5 h-5 mr-2" />
                  +66 (81) 838-3869
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Confidentiality Assurance */}
      <section className="py-20 bg-[#000A22] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Scale className="w-16 h-16 text-[#D4A74A] mx-auto" />
            <h2 className="text-3xl font-bold">Your Privacy is Protected</h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              All consultations and case details are handled with the strictest confidentiality. We understand the sensitive nature of criminal matters and maintain complete discretion throughout the legal process.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 bg-red-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <p className="text-2xl font-bold mb-6">Don't wait. Contact us now for immediate legal assistance.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              className="bg-white text-red-900 hover:bg-gray-100 font-semibold px-8 py-6 text-lg transition-all duration-300"
              asChild
            >
              <a href="tel:+6638412415">
                <Phone className="w-5 h-5 mr-2" />
                Call Now: +66 (38) 412-415
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CriminalPage;
